import { ActivatedRoute, Router } from '@angular/router'
import { CommonModule, NgOptimizedImage } from '@angular/common'
import { AddressPipe } from '../pipes/address.pipe'
import { BookingDetails } from '../domain/BookingDetails'
import { BookingDetailsViewModel } from '../booking-summary/BookingDetailsViewModel'
import { BookingService } from '../services/booking.service'
import { Component, Input, OnDestroy, OnInit, Output } from '@angular/core'
import {
    ConfirmCancellationComponent,
} from '../confirm-cancellation/confirm-cancellation.component'
import { ConfirmCardComponent } from '../confirm-payment/confirm-card.component'
import { GetDirectionsService } from '../services/get-directions.service'
import { MenuLinkType } from '../domain/MenuLinkType'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import {
    BehaviorSubject,
    Observable,
    Subject,
    filter,
    finalize,
    map, mergeMap, of, take, takeUntil,
} from 'rxjs'
import { catchError } from 'rxjs/operators'
import { ProductLibrary } from '../domain/ProductLibrary'
import { ProductLibraryService } from '../services/product-library.service'
import { WindowService } from '../services/window/window.service'
import { BookingAction, LoggingService } from '../services/logging.service'
import { CurrencyUnitPipe } from '../pipes/currency-unit.pipe'
import { MenuChoicesProgressComponent } from '../menu-choices-progress/menu-choices-progress.component'
import { QuillViewComponent } from 'ngx-quill'
import { environment } from '../../environments/environment'

@Component({
    selector: 'app-booking-details',
    standalone: true,
    imports: [
        AddressPipe,
        CommonModule,
        CurrencyUnitPipe,
        MenuChoicesProgressComponent,
        NgOptimizedImage,
        QuillViewComponent,
    ],
    styleUrls: ['./booking-details.component.sass'],
    templateUrl: './booking-details.component.html',
})
export class BookingDetailsComponent implements OnInit, OnDestroy {

    @Input() resumePaymentConfirmation = false
    @Input() viewModel!: Observable<BookingDetailsViewModel>
    @Output() bookAgainSelected = new Subject<void>()
    partySizePluralMapping = {
        '=1': '1 person',
        other: '# people',
    }
    readonly menuLinkTypes$ = new BehaviorSubject<Map<string, MenuLinkType> | null>(null)
    isCancelling = false
    cancellationError: string | null = null
    private onDestroy$ = new Subject<boolean>()

    constructor(
        private bookingService: BookingService,
        private getDirectionService: GetDirectionsService,
        private loggingService: LoggingService,
        private modalService: NgbModal,
        private productLibraryService: ProductLibraryService,
        private windowService: WindowService,
        private route: ActivatedRoute,
        private router: Router
    ) { }

    ngOnInit() {
        this.startCancelIfQueryParameterIncluded()
        this.loadProductLibraryIfNeeded()
        if (this.resumePaymentConfirmation) {
            this.viewModel.pipe(take(1), takeUntil(this.onDestroy$))
                .subscribe(viewModel => this.confirmWithCard(viewModel))
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next(true)
        this.onDestroy$.complete()
        this.menuLinkTypes$.complete()
    }

    bookAgainClicked(_: BookingDetailsViewModel) {
        this.bookAgainSelected.next()
    }

    fillMenuChoicesClicked(viewModel: BookingDetailsViewModel, linkType: MenuLinkType = MenuLinkType.SingleOrganiser): void {
        const docket = viewModel.details.docket
        if (!docket) {
            return
        }
        const menuPath = linkType === MenuLinkType.PerCover ? 'individual' : 'edit'
        this.router.navigate(['booking', viewModel.details.bookingId, 'menu', menuPath])
    }

    private loadProductLibraryIfNeeded() {
        this.viewModel.pipe(
            takeUntil(this.onDestroy$),
            filter(vm => [vm, ...vm.additionalBookings].some(booking => booking.details.docket !== null)),
            mergeMap(vm => this.productLibraryService.getForVenueId(vm.details.venueId).pipe(
                catchError(() => of(null))
            ))
        ).subscribe((productLibrary: ProductLibrary | null): void => {
            const linkTypes = new Map<string, MenuLinkType>()
            for (const menu of productLibrary?.menus ?? []) {
                linkTypes.set(menu.id, menu.linkType)
            }
            this.menuLinkTypes$.next(linkTypes)
        })
    }

    modifyBookingClicked(details: BookingDetails) {
        const url = details.modifyBookingPath
        if (url === null) {
            return
        }
        this.windowService.navigateToUrl(url)
    }

    getDirectionsClicked(viewModel: BookingDetailsViewModel) {
        const addressLines = viewModel.details.venueAddress.lines()
        const directionsURL = this.getDirectionService.createURL(addressLines)
        this.windowService.openWindowAtUrl(directionsURL)
    }

    viewBookingClicked(bookingDetails: BookingDetails) {
        const url = new URL(`/booking/${encodeURIComponent(bookingDetails.bookingId)}`, environment.landingPageBaseUrl)
        this.windowService.openWindowAtUrl(url.toString())
    }

    cancelBookingClicked(viewModel: BookingDetailsViewModel) {
        if (!viewModel.showCancelButton || this.isCancelling) {
            return
        }
        const modal = this.modalService.open(ConfirmCancellationComponent)
        const component = modal.componentInstance as ConfirmCancellationComponent
        component.bookingDetails = viewModel.details
        component.cancelSelected
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
                this.loggingService.trackAction(BookingAction.CancelBookingDismissed, {
                    bookingId: viewModel.details.bookingId,
                })
                modal.dismiss()
            })
        component.confirmSelected
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((cancellationReason) => {
                this.loggingService.trackAction(BookingAction.CancelBookingConfirmed, {
                    bookingId: viewModel.details.bookingId,
                })
                modal.close()
                this.cancelBooking(viewModel.details.bookingId, cancellationReason?.id)
            })
    }

    confirmWithCard(viewModel: BookingDetailsViewModel) {
        const modal = this.modalService.open(ConfirmCardComponent, { backdrop: 'static', keyboard: false })
        const component = modal.componentInstance as ConfirmCardComponent
        component.bookingDetails = viewModel.details
        modal.closed
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((bookingDetails: BookingDetails) => {
                this.viewModel = of(this.makeViewModel(bookingDetails))
            })
        modal.dismissed
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
                this.viewModel = this.bookingService
                    .getBookingDetails(viewModel.details.bookingId)
                    .pipe(
                        map(details => this.makeViewModel(details)),
                        takeUntil(this.onDestroy$),
                        catchError(error => {
                            console.error('Failed to reload booking details after card confirmation dismissal', error)
                            return of(this.makeViewModel(viewModel.details))
                        }),
                    )
            })
    }

    private startCancelIfQueryParameterIncluded() {
        this.route.queryParams.pipe(
            takeUntil(this.onDestroy$),
            filter(params => params['action'] === 'cancel'),
            mergeMap(() => this.viewModel)
        )
            .subscribe(viewModel => this.cancelBookingClicked(viewModel))
    }

    private cancelBooking(bookingId: string, cancellationReasonId?: string) {
        if (this.isCancelling) return
        this.isCancelling = true
        this.cancellationError = null
        this.bookingService.cancelBooking(bookingId, cancellationReasonId).pipe(
            takeUntil(this.onDestroy$),
            finalize(() => this.isCancelling = false),
        ).subscribe({
            next: details => this.viewModel = of(this.makeViewModel(details)),
            error: () => this.cancellationError = 'We couldn\'t confirm your cancellation.',
        })
    }

    private makeViewModel(details: BookingDetails): BookingDetailsViewModel {
        return new BookingDetailsViewModel(details, false, false)
    }

    protected readonly ConfirmCardComponent = ConfirmCardComponent
}
